<template>
  <BasePage container-style="max-width: 1200px" header-style="max-width: 1200px">
    <template #page-header:subtitle>
      <BackButton class="mr-4" ref="backButton" :fallback-to="{ name: 'chit' }" />
      {{ `${route.meta.title}` }}
    </template>

    <template #page-header:bottom>
      <ErrorAlert
        v-if="error != null"
        dense
        :error="error"
        @clearErrors="clearErrors"
        class="mb-0 mt-4 rounded"
      />
    </template>

    <v-progress-linear color="primary" indeterminate v-if="isFetchPending" />

    <template v-else-if="chit">
      <StateCard
        v-if="chitVisualState"
        :color="chitVisualState.color"
        :display="chitVisualState.text"
        :icon="chitVisualState.icon!"
      >
        <template #actions>
          <ChitApprovedStateActions
            v-if="chitVisualState.state == ChitState.Approved"
            :loading="loading"
            :state="chit.state"
            @export-chit-pdf-pressed="downloadChitPdf"
          />
          <ChitAssignedStateActions
            v-else-if="chitVisualState.state == ChitState.Assigned"
            :can-submit="
              Boolean(chit.canSubmit() && currentUser?.hasPermission('chits.submit_chit'))
            "
            :loading="loading"
            @export-chit-pdf-pressed="downloadChitPdf"
            @submit-pressed="openSubmitFormDialog()"
            @edit-chit-pressed="openEditChitDialog()"
            @delete-chit-pressed="openDeleteChitDialog()"
          />
          <ChitSubmittedStateActions
            :can-approve="
              Boolean(chit.canApprove() && currentUser?.hasPermission('chits.approve_chit'))
            "
            v-else-if="chitVisualState.state == ChitState.Submitted"
            :loading="loading"
            @export-chit-pdf-pressed="downloadChitPdf"
            @approved="approve()"
            @edit-chit-pressed="openEditChitDialog()"
            @delete-chit-pressed="openDeleteChitDialog()"
          />
        </template>
      </StateCard>

      <ChitDetail class="mt-5" :chit="chit" />
    </template>

    <ChitFormDialog
      v-model:dialog="chitEditDialog"
      :chit="chit"
      :is-edit="true"
      @saved-chit="updateChitCache()"
    />

    <ChitSubmitFormDialog
      v-if="chit"
      v-model:dialog="submitChitDialog"
      :chit-number="chit.chit_number!"
      @submitted-chit="updateChitCache()"
    />

    <ConfirmationDialog v-model="chitDeleteDialog" v-model:error="deleteError" title="Delete Chit">
      <template v-slot:message>
        Are you sure you want to delete Chit
        <span class="text-medium-high-emphasis font-weight-bold">#{{ chit!.chit_number }}</span
        >?
        <br />
        <div class="pt-2">This action cannot be undone.</div>
      </template>

      <template v-slot:actions>
        <v-spacer></v-spacer>

        <v-btn
          color="error"
          variant="flat"
          class="text-capitalize"
          :loading="deleteInProgress"
          @click.stop="removeChit(chit!)"
        >
          Delete
        </v-btn>
      </template>
    </ConfirmationDialog>
  </BasePage>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'

import { useApproveChit, useDeleteChit, useExportChitPdf, useFetchChit } from '@/composables/chit'
import { useAuthStore, useChitStore } from '@/stores'
import { useQueryClient } from '@tanstack/vue-query'

import type { ISystemError } from '@/models/error'
import { ChitState, type IChit } from '@/models/chit'
import { createFile } from '@/utils/helpers'

import BasePage from '@/components/base/BasePage.vue'
import BackButton from '@/components/common/BackButton.vue'
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import ErrorAlert from '@/components/common/ErrorAlert.vue'
import ChitAssignedStateActions from '@/components/chits/state/ChitAssignedStateActions.vue'
import ChitSubmittedStateActions from '@/components/chits/state/ChitSubmittedStateActions.vue'
import ChitFormDialog from '@/components/chits/ChitFormDialog.vue'
import ChitDetail from '@/components/chits/ChitDetail.vue'
import StateCard from '@/components/common/StateLayoutCard.vue'
import ChitSubmitFormDialog from '@/components/chits/ChitSubmitFormDialog.vue'
import ChitApprovedStateActions from '@/components/chits/state/ChitApprovedStateActions.vue'

// CORE
const queryClient = useQueryClient()
const route = useRoute()
const router = useRouter()

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

// Managing multiple errors into one property
const error = ref<ISystemError | null>(null)
const chitDeleteDialog = ref(false)
const chitEditDialog = ref(false)

const chitStore = useChitStore()
const { getChitVisualByState } = storeToRefs(chitStore)

const chitVisualState = computed(() => chit.value && getChitVisualByState.value(chit.value.state))

const {
  chit,
  isPending: isFetchPending,
  error: fetchError,
  queryKey
} = useFetchChit(Number(route.params.id))

function openDeleteChitDialog() {
  chitDeleteDialog.value = true
}
function openEditChitDialog() {
  chitEditDialog.value = true
}

const submitChitDialog = ref(false)
function openSubmitFormDialog() {
  submitChitDialog.value = true
}

const {
  isPending: exportInProgress,
  error: exportError,
  mutate: exportChitPdfMutate,
  reset: clearExportErrors
} = useExportChitPdf()

function downloadChitPdf() {
  clearExportErrors()
  exportChitPdfMutate(chit.value!.chit_number!, {
    onSuccess: (blob: Blob) => {
      const fileName = `chit-report-${chit.value!.chit_number}-${Date.now()}.pdf`
      createFile(blob, fileName, 'pdf')
    }
  })
}

const { isPending: isApproveChitPending, mutate: approveChit } = useApproveChit()

function approve() {
  clearErrors()
  approveChit(chit.value!.chit_number!, {
    onSuccess: () => {
      updateChitCache()
    },
    onError(approveError) {
      error.value = approveError
    }
  })
}

function clearErrors() {
  error.value = null
}

const backButton = ref<typeof BackButton>()

const { isPending: deleteInProgress, error: deleteError, mutate: deleteChit } = useDeleteChit()

function updateChitCache() {
  // TODO - LH - 2024-02-20 - Should find a better way to invalidate both queries
  // invalidate current incident chit cache to update this cache
  queryClient.invalidateQueries({ queryKey: queryKey.value })
  // invalidate list cache regarding incident
  queryClient.invalidateQueries({ queryKey: ref(['chits']) })
}

function removeChit(chit: IChit) {
  clearErrors()
  deleteChit(chit.chit_number!, {
    onSuccess: () => {
      chitDeleteDialog.value = false
      queryClient.invalidateQueries({ queryKey: ref(['chits']) })
      router.push(backButton.value?.backPath)
    }
  })
}

const loading = computed(() => isApproveChitPending.value || exportInProgress.value)
watch([fetchError, exportError], ([fetchErr, exportErr]) => {
  error.value = fetchErr || exportErr
})
</script>
